import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Socket } from 'socket.io-client';

import { UserStatus } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import {
  ApiChatResponse,
  ApiCreateMessageDto,
  ApiMessageResponse,
  ApiParticipantResponse,
} from '@/shared/api/generated/data-contracts';
import { useApi } from '@/shared/api/hooks/useApi';
import { useSound } from '@/shared/hooks/useSound';
import { useChatSocket } from '@/shared/services/socket/chat.service';
import { compareDates } from '@/shared/utils/compare';
import { useModalsStore } from '@/widgets/modals';

import { useCasesStore } from '../stores/cases.store';
import { useChatStore } from '../stores/chat.store';
import { useMembersStore } from '../stores/members.store';

import { useCases } from './useCases';
import { useDocuments } from './useDocuments';
import { useMembers } from './useMembers';
import { useTasks } from './useTasks';

const NOTIFICATION_PING = '/assets/widgets/modals/vfoChat/ping.mp3';

export const useChat = () => {
  const api = useApi();
  const notices = useNotices();
  const socket = useChatSocket();
  const { data: modalData } = useModalsStore();
  const { fetchMembers, currentUser } = useMembers();
  const { fetchDocuments } = useDocuments();
  const { updateMember, members, isInitialized: isMembersInitialized } = useMembersStore();
  const { isInitialized: isCasesInitialized } = useCasesStore();
  const { fetchCases, findByChatId, cases } = useCases();
  const { removeCase } = useCasesStore();
  const { playSound } = useSound(NOTIFICATION_PING);
  const { fetchTasks } = useTasks();
  const {
    hasNotificationSounds,
    chats,
    isLoading,
    activeChatId,
    unreadCounts,
    participantStatus,
    setIsInitialized,
    setChats,
    setLoading,
    setActiveChatId,
    setUnreadCounts,
    addMessage,
    setMessages,
    removeChat,
    markChatAsRead: markChatAsReadAction,
    incrementUnreadCount,
    prependMessages,
    moveChatToTop,
    moveChatAfterUnread,
    setHasNotificationSounds,
    addParticipantToChat,
    removeParticipantFromChat,
  } = useChatStore();

  const fetchChats = useCallback(async () => {
    setLoading(true);
    setIsInitialized(true);

    const response = await api.chatsFindAll();

    if (response.data) {
      const chatsData = response.data.sort((a, b) => compareDates(b.updatedAt, a.updatedAt));
      // TODO: calculate unread count when backend is ready
      const newUnreadCounts: Record<string, number> = { ...unreadCounts };
      chatsData.forEach((chat: ApiChatResponse) => {
        newUnreadCounts[chat.id] = newUnreadCounts[chat.id] ?? 0;

        chat.participants.forEach((p) => {
          updateMember(p.profileId, {
            status: p.status,
            lastActiveDate:
              p.status === UserStatus.OFFLINE && p.lastSeenAt
                ? p.lastSeenAt
                : new Date().toISOString(),
          });
        });
      });
      setChats(chatsData);
      setUnreadCounts(newUnreadCounts);
      setLoading(false);
      return;
    }
    notices.error('Failed to fetch chats');
    setLoading(false);
  }, [api, notices, unreadCounts, updateMember, setChats, setUnreadCounts, setLoading]);

  const selectChat = useCallback(
    (chatId: string) => {
      setActiveChatId(chatId);
    },
    [setActiveChatId],
  );

  const sendMessage = useCallback(
    async (chatId: string, message: Omit<ApiCreateMessageDto, 'chatId'>) => {
      const response = await api.chatsMessageCreate({
        ...message,
        chatId,
      });

      if (response.error) {
        notices.error('Failed to send message');
      }
    },
    [api, notices],
  );

  const getMessages = useCallback(
    async (chatId: string, options?: { limit?: number; offset?: number }) => {
      const { limit, offset } = options || {};
      const response = await api.chatsMessageFindAll({
        chatId,
        limit,
        offset,
      });

      if (response.data && !response.error) {
        const messages = response.data.reverse();
        if (offset && offset > 0) {
          prependMessages(chatId, messages);
        } else {
          setMessages(chatId, messages);
        }
        return messages;
      }
      notices.error('Failed to fetch messages');
      return undefined;
    },
    [api, notices, prependMessages, setMessages],
  );

  const markChatAsRead = useCallback(
    (chatId: string) => {
      markChatAsReadAction(chatId);
      moveChatAfterUnread(chatId);
    },
    [markChatAsReadAction, moveChatAfterUnread],
  );

  const deleteChat = useCallback(
    async (chatId: string) => {
      const response = await api.deleteChat(chatId);

      if (response.error) {
        notices.error('Failed to delete chat');
        return;
      }

      removeChat(chatId);
    },
    [api, notices],
  );

  const findByCaseId = useCallback(
    (caseId: string) => {
      return chats.find((chat) =>
        cases.find((caseData) => caseData.id === caseId && caseData.chatId === chat.id),
      );
    },
    [cases, chats],
  );

  const handleMessageCreated = useCallback(
    (message: ApiMessageResponse) => {
      addMessage(message.chatId, message);
      moveChatToTop(message.chatId);
      if (
        message.profileId !== currentUser?.profileId &&
        (activeChatId !== message.chatId || !modalData.find((item) => item.type === 'VfoChatModal'))
      ) {
        incrementUnreadCount(message.chatId);

        if (hasNotificationSounds && !message.isSystem) {
          playSound();
        }
      }
      if (message.attachments.length) {
        fetchCases();
        fetchDocuments();
      }
    },
    [
      activeChatId,
      addMessage,
      incrementUnreadCount,
      moveChatToTop,
      modalData,
      hasNotificationSounds,
      playSound,
    ],
  );

  const handleChatCreated = useCallback(() => {
    fetchChats();
    // TODO: remove fetchMembers and fetchCases when case service socket is ready
    fetchMembers();
    fetchCases();
  }, [fetchChats, fetchMembers, fetchCases]);

  const handleChatDeleted = useCallback(
    (chat: ApiChatResponse) => {
      removeChat(chat.id);
      const caseData = findByChatId(chat.id);
      if (caseData) {
        removeCase(caseData.id);
      }
    },
    [removeChat, removeCase, findByChatId],
  );

  const refetch = useCallback(() => {
    return Promise.allSettled([
      fetchChats(),
      fetchCases(),
      fetchMembers(),
      fetchDocuments(),
      fetchTasks(),
    ]);
  }, [fetchCases, fetchChats, fetchMembers, fetchDocuments, fetchTasks]);

  const handleParticipantStatusChanged = useCallback(
    async (participants: ApiParticipantResponse[]) => {
      const isNewParticipant = participants.some(
        (p) => !members.find((m) => m.profileId === p.profileId),
      );
      if (isNewParticipant) {
        await refetch();
      }
      participants?.forEach((p) => {
        updateMember(p.profileId, {
          status: p.status,
        });
      });
    },
    [members, refetch],
  );

  const handleParticipantAdded = useCallback(
    (participant: ApiParticipantResponse) => {
      if (currentUser?.profileId === participant.profileId) {
        refetch();
        return;
      }
      addParticipantToChat(participant.chatId, participant);
    },
    [currentUser, addParticipantToChat],
  );

  const handleParticipantRemoved = useCallback(
    (participant: ApiParticipantResponse) => {
      if (currentUser?.profileId === participant.profileId) {
        refetch();
        return;
      }
      removeParticipantFromChat(participant.chatId, participant.profileId);
    },
    [currentUser, removeParticipantFromChat],
  );

  const messageCreatedRef = useRef(handleMessageCreated);
  const chatCreatedRef = useRef(handleChatCreated);
  const chatDeletedRef = useRef(handleChatDeleted);
  const participantStatusChangedRef = useRef(handleParticipantStatusChanged);
  const participantAddedRef = useRef(handleParticipantAdded);
  const participantRemovedRef = useRef(handleParticipantRemoved);
  const refetchRef = useRef(refetch);

  useEffect(() => {
    messageCreatedRef.current = handleMessageCreated;
    chatCreatedRef.current = handleChatCreated;
    chatDeletedRef.current = handleChatDeleted;
    participantStatusChangedRef.current = handleParticipantStatusChanged;
    participantAddedRef.current = handleParticipantAdded;
    participantRemovedRef.current = handleParticipantRemoved;
    refetchRef.current = refetch;
  }, [
    handleMessageCreated,
    handleChatCreated,
    handleChatDeleted,
    handleParticipantAdded,
    participantAddedRef,
    refetch,
    members,
  ]);

  // subscribe once and don't reconnect when function ref changes
  const subscribeToWebSocket = useCallback((subSocket: Socket) => {
    subSocket.on('message:created', (data) => messageCreatedRef.current(data));
    subSocket.on('chat:created', () => chatCreatedRef.current());
    subSocket.on('chat:deleted', (data) => chatDeletedRef.current(data));
    subSocket.on('participant:added', (data) => participantAddedRef.current(data));
    subSocket.on('participant:removed', (data) => participantRemovedRef.current(data));
    subSocket.on('participants:status_changed', (data) =>
      participantStatusChangedRef.current(data),
    );
  }, []);

  useEffect(() => {
    // get the live store state to avoid race conditions
    const { isInitialized } = useChatStore.getState();
    if (!isMembersInitialized || !isCasesInitialized || isInitialized || !socket) return;

    fetchChats();
    subscribeToWebSocket(socket);
  }, [isMembersInitialized, isCasesInitialized, socket, fetchChats, subscribeToWebSocket]);

  return useMemo(
    () => ({
      chats,
      isLoading,
      activeChatId,
      unreadCounts,
      participantStatus,
      hasNotificationSounds,
      fetchChats,
      selectChat,
      deleteChat,
      sendMessage,
      markChatAsRead,
      subscribeToWebSocket,
      getMessages,
      findByCaseId,
      setHasNotificationSounds,
      refetch,
    }),
    [
      chats,
      isLoading,
      activeChatId,
      unreadCounts,
      participantStatus,
      hasNotificationSounds,
      fetchChats,
      selectChat,
      deleteChat,
      sendMessage,
      markChatAsRead,
      subscribeToWebSocket,
      getMessages,
      findByCaseId,
      setHasNotificationSounds,
      refetch,
    ],
  );
};
